import React, { useState, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";

import Box from "@material-ui/core/Box";
import Paper from "@material-ui/core/Paper";
import MenuItem from "@material-ui/core/MenuItem";
import {
  type GridSelectionModel,
  type GridFilterModel,
  type GridSortModel,
  GridLinkOperator,
} from "@material-ui/x-grid";

import { filteredSalesSelector } from "src/store/sale/selector";
import {
  effectiveUserTimeZoneSelector,
  userGetInventoryTypeSelector,
} from "src/store/system/selector";
import {
  mappedTransactionSelector,
  filteredExpensesSelector,
} from "src/store/transaction/selector";
import TimeFilter, {
  Select,
  getPeriodFromPreset,
} from "src/components/TimeFilter";
import { useDateRange } from "src/utils/timeFilter";
import TableHeader, { TableHeaderButton } from "src/components/TableHeader";
import XGrid from "src/components/ConfiguredXGrid";
import CSVFormattedXGrid from "src/components/CSVFormattedXGrid";
import {
  createTableToolbar,
  DefaultFeatures,
} from "src/components/TableToolbar";
import { calendarYmdInTimeZone } from "src/utils/timezone";
import { useFullscreen } from "src/utils/fullscreen";
import { useRecordEvent } from "src/hooks/useRecordEvent";
import BulkEditDialog from "src/pages/SalesPage/BulkEditDialog";
import BulkEditTransactionsDialog from "../Expenses/BulkEditDialog";
import BulkEditPanel from "../Expenses/BulkEditPanel";
import { setActiveDialog } from "src/store/adminHtml/actions";
import { deleteMappedTransactions } from "src/apiService/modules/mapped-transactions";
import ConfirmDialog from "src/components/ConfirmDialog";
import type { MappedTransaction } from "src/interfaces/transaction.interface";

import {
  ReturnColumns,
  ReturnColumnsCash,
  TransactionSalesColumns,
  TransactionSalesOtherColumns,
} from "./columns";
import Edit, { type DialogState } from "./Edit";

const ReturnsTableToolbar = createTableToolbar({
  features: {
    ...DefaultFeatures,
    Fullscreen: true,
  },
  options: { fileName: "My Reseller Genie - Returns" },
});

const SalesViews = ["item-detail", "unreviewed", "missing-dates"];

function Refunds() {
  const recordEvent = useRecordEvent();

  const dispatch = useDispatch();
  const timeZone = useSelector(effectiveUserTimeZoneSelector);
  const { timeOption: timeFilter, setDateRange } = useDateRange("transaction");
  const [view, setView] = useState("item-detail");
  const isSalesTable = SalesViews.includes(view);
  const filters = useMemo(() => {
    const models: GridFilterModel[] = [];
    const period =
      timeFilter.type === "custom"
        ? timeFilter.period
        : getPeriodFromPreset(timeFilter, timeZone);

    if (SalesViews.includes(view)) {
      if (view === "missing-dates") {
        models.push(
          {
            items: [
              {
                columnField: "is_return",
                operatorValue: "is",
                value: "true",
              },
            ],
          },
          {
            items: [
              {
                columnField: "sale_date",
                operatorValue: "isEmpty",
              },
              {
                columnField: "return_date",
                operatorValue: "isEmpty",
              },
              {
                columnField: "list_date",
                operatorValue: "isEmpty",
              },
              {
                columnField: "purchase_date",
                operatorValue: "isEmpty",
              },
            ],
            linkOperator: GridLinkOperator.Or,
          }
        );
      } else {
        const model = {
          items: [
            {
              columnField: "is_return",
              operatorValue: "is",
              value: "true",
            },
            {
              columnField: "return_date",
              operatorValue: "onOrAfter",
              value: calendarYmdInTimeZone(period.start, timeZone),
            },
            {
              columnField: "return_date",
              operatorValue: "onOrBefore",
              value: calendarYmdInTimeZone(period.end, timeZone),
            },
          ],
        };

        if (view === "unreviewed") {
          model.items.push({
            columnField: "unreviewed",
            operatorValue: "is",
            value: "true",
          });
        }

        models.push(model);
      }
    } else {
      const model = {
        items: [
          {
            columnField: "date",
            operatorValue: "onOrAfter",
            value: calendarYmdInTimeZone(period.start, timeZone),
          },
          {
            columnField: "date",
            operatorValue: "onOrBefore",
            value: calendarYmdInTimeZone(period.end, timeZone),
          },

          {
            columnField: "amount",
            operatorValue: ">",
            value: "0",
          },
        ],
      };

      if (view === "other-refunds") {
        model.items.push({
          columnField: "type",
          operatorValue: "notEquals",
          value: "sale",
        });
      } else {
        model.items.push({
          columnField: "type",
          operatorValue: "equals",
          value: "income",
        });
      }

      models.push(model);
    }
    return models;
  }, [view, timeFilter, timeZone]);
  const refunds = useSelector((state) =>
    isSalesTable
      ? filteredSalesSelector(state, filters)
      : view === "other-refunds"
      ? filteredExpensesSelector(state, filters)
      : mappedTransactionSelector(state, filters)
  );
  const returnsSectionRef = useRef<HTMLDivElement>();
  const requestFullscreen = useFullscreen();
  const inventoryTypeIsCash =
    useSelector(userGetInventoryTypeSelector) === "cash";
  const [sortModel, setSortModel] = useState<GridSortModel>([
    { field: "return_date", sort: "desc" },
  ]);
  const [expensesSortModel, setExpensesSortModel] = useState<GridSortModel>([
    { field: "date", sort: "desc" },
  ]);
  const [transactionSortModel, setTransactionSortModel] =
    useState<GridSortModel>([{ field: "date", sort: "desc" }]);
  const [selectionModel, setSelectionModel] = useState<GridSelectionModel>([]);
  const [dialog, setDialog] = useState<
    | { type: "bulk-upload" }
    | {
        type: "delete-many";
        items: Pick<MappedTransaction, "collection" | "collection_id">[];
      }
    | { type: "bulk-edit-transactions" }
    | { type: "bulk-edit-panel" }
    | DialogState
  >();

  const { editableTransactionIds, readOnlyCount } = useMemo(() => {
    const ids: string[] = [];
    let readOnly = 0;
    for (const id of selectionModel) {
      const row = refunds.find((r) => r.id === id);
      if (!row) continue;
      if (row.read_only) {
        readOnly += 1;
      } else {
        ids.push(String(id));
      }
    }
    return { editableTransactionIds: ids, readOnlyCount: readOnly };
  }, [selectionModel, refunds]);

  return (
    <div>
      <TimeFilter
        value={timeFilter}
        onChange={(v) => setDateRange(v)}
        disabled={view === "missing-dates"}
      >
        <Select
          label="View"
          value={view}
          onChange={(e) => {
            setSelectionModel([]);
            const view = e.target.value as string;
            setView(view);
            setSelectionModel([]);
            recordEvent({
              type: "feature-adoption",
              feature: "transaction-details-view",
              element: "refunds",
              view: view,
            });
          }}
        >
          <MenuItem value="all">All Transactions</MenuItem>
          <MenuItem value="item-detail">Item Detail</MenuItem>
          <MenuItem value="unreviewed">Unreviewed</MenuItem>
          <MenuItem value="missing-dates">Missing Dates</MenuItem>
          <MenuItem value="other-refunds">Other Refunds</MenuItem>
        </Select>
      </TimeFilter>
      <Box
        {...({ ref: returnsSectionRef } as any)}
        component={Paper}
        display="flex"
        flexDirection="column"
        minHeight={730}
        height="auto"
        p={2.5}
      >
        <TableHeader
          title="Returns / Refunds / Cancellations"
          hideChildrenInFullscreen
        >
          {view !== "all" ? (
            <TableHeaderButton
              color="primary"
              onClick={() => {
                setDialog(
                  view === "other-refunds"
                    ? { type: "bulk-edit-panel" }
                    : { type: "bulk-upload" }
                );
              }}
              disabled={
                view === "other-refunds" &&
                (selectionModel.length === 0 ||
                  editableTransactionIds.length === 0)
              }
              tooltip={
                view === "other-refunds"
                  ? selectionModel.length === 0
                    ? "Select transactions to bulk edit."
                    : editableTransactionIds.length === 0
                    ? "Beginning Balance journals can't be bulk edited."
                    : undefined
                  : undefined
              }
            >
              {view === "other-refunds"
                ? "Bulk Edit Transactions"
                : "Bulk Edit Refunds"}
            </TableHeaderButton>
          ) : null}
          <TableHeaderButton
            colorType="error"
            disabled={selectionModel.length === 0}
            onClick={() => {
              if (!selectionModel.length) return;
              setDialog({
                type: "delete-many",
                items: selectionModel.reduce((all, id) => {
                  const item = refunds.find((r) => r.id === id);
                  if (item) {
                    all.push({
                      collection: item.collection || "sale",
                      collection_id: item.collection_id || item.id,
                    });
                  }
                  return all;
                }, [] as Pick<MappedTransaction, "collection_id" | "collection">[]),
              });
              setSelectionModel([]);
            }}
          >
            Delete Selected
          </TableHeaderButton>
        </TableHeader>

        {isSalesTable ? (
          <XGrid
            key="sales"
            checkboxSelection
            tableName="returns"
            Component={CSVFormattedXGrid}
            rows={refunds}
            columns={inventoryTypeIsCash ? ReturnColumnsCash : ReturnColumns}
            headerHeight={35}
            rowHeight={35}
            components={{
              Toolbar: ReturnsTableToolbar,
            }}
            componentsProps={{
              toolbar: {
                onFullscreenClick: () => {
                  if (document.fullscreen) {
                    document.exitFullscreen();
                    return;
                  }
                  if (!returnsSectionRef.current) return;
                  requestFullscreen(returnsSectionRef.current);
                  recordEvent({
                    type: "feature-adoption",
                    feature: "fullscreen",
                    element: "refunds-table",
                  });
                },
                onQuickFilterBlur: (e) => {
                  const text = e.target.value;
                  if (!text) return;
                  recordEvent({
                    type: "feature-adoption",
                    feature: "quick-filter",
                    element: "refunds",
                    text,
                  });
                },
              },
            }}
            selectionModel={selectionModel}
            onSelectionModelChange={(selectionModel: GridSelectionModel) =>
              setSelectionModel(selectionModel)
            }
            onCellDoubleClick={(e) => setDialog({ type: "edit", id: e.row.id })}
            onFilterModelChange={(model) => {
              recordEvent({
                type: "feature-adoption",
                feature: "table-filter",
                element: "refunds",
                filter: JSON.stringify(model),
              });
            }}
            sortModel={sortModel}
            onSortModelChange={(model) => setSortModel(model)}
          />
        ) : view === "other-refunds" ? (
          <XGrid
            key="expenses"
            checkboxSelection
            tableName="returns-transactions-other"
            Component={CSVFormattedXGrid}
            rows={refunds.filter((s) => s.accountDoc?.type?.[0] === "income")}
            columns={TransactionSalesOtherColumns}
            headerHeight={35}
            rowHeight={35}
            components={{
              Toolbar: ReturnsTableToolbar,
            }}
            componentsProps={{
              toolbar: {
                onFullscreenClick: () => {
                  if (document.fullscreen) {
                    document.exitFullscreen();
                    return;
                  }
                  if (!returnsSectionRef.current) return;
                  requestFullscreen(returnsSectionRef.current);
                  recordEvent({
                    type: "feature-adoption",
                    feature: "fullscreen",
                    element: "refunds-table",
                  });
                },
                onQuickFilterBlur: (e) => {
                  const text = e.target.value;
                  if (!text) return;
                  recordEvent({
                    type: "feature-adoption",
                    feature: "quick-filter",
                    element: "refunds",
                    text,
                  });
                },
              },
            }}
            onFilterModelChange={(model) => {
              recordEvent({
                type: "feature-adoption",
                feature: "table-filter",
                element: "refunds",
                filter: JSON.stringify(model),
              });
            }}
            sortModel={expensesSortModel}
            onSortModelChange={(model) => setExpensesSortModel(model)}
            onCellDoubleClick={(e) => {
              if (e.row?.journal_entry_id) {
                dispatch(
                  setActiveDialog("add_journal", {
                    id: e.row.journal_entry_id,
                  })
                );
                return;
              }
              if (e.row?.split_group_id) {
                dispatch(
                  setActiveDialog("edit_split", {
                    split_group_id: e.row.split_group_id,
                    id: e.row.collection_id || e.row.id,
                  })
                );
                return;
              }
              if (e.row.collection === "transaction") {
                dispatch(
                  setActiveDialog("edit_transaction", {
                    id: e.row.collection_id,
                  })
                );
                return;
              }
              if (e.row.collection === "inventory") {
                dispatch(
                  setActiveDialog("edit_inventory", {
                    id: e.row.collection_id,
                  })
                );
                return;
              }
              if (e.row.collection === "sale") {
                dispatch(
                  setActiveDialog("edit_sale", {
                    id: e.row.collection_id,
                  })
                );
                return;
              }
              if (e.row?.type === "sale") {
                dispatch(
                  setActiveDialog("edit_sale", {
                    id: e.row.sale_id,
                  })
                );
                return;
              }
              dispatch(
                setActiveDialog("edit_transaction", {
                  id: e.row.id,
                })
              );
            }}
            selectionModel={selectionModel}
            onSelectionModelChange={(selectionModel: GridSelectionModel) => {
              setSelectionModel(selectionModel);
            }}
          />
        ) : (
          <XGrid
            selectionModel={selectionModel}
            onSelectionModelChange={(selectionModel: GridSelectionModel) =>
              setSelectionModel(selectionModel)
            }
            key="transactions"
            checkboxSelection
            tableName="returns-transactions"
            Component={CSVFormattedXGrid}
            rows={refunds}
            columns={TransactionSalesColumns}
            headerHeight={35}
            rowHeight={35}
            components={{
              Toolbar: ReturnsTableToolbar,
            }}
            componentsProps={{
              toolbar: {
                onFullscreenClick: () => {
                  if (document.fullscreen) {
                    document.exitFullscreen();
                    return;
                  }
                  if (!returnsSectionRef.current) return;
                  requestFullscreen(returnsSectionRef.current);
                  recordEvent({
                    type: "feature-adoption",
                    feature: "fullscreen",
                    element: "refunds-table",
                  });
                },
                onQuickFilterBlur: (e) => {
                  const text = e.target.value;
                  if (!text) return;
                  recordEvent({
                    type: "feature-adoption",
                    feature: "quick-filter",
                    element: "refunds",
                    text,
                  });
                },
              },
            }}
            onFilterModelChange={(model) => {
              recordEvent({
                type: "feature-adoption",
                feature: "table-filter",
                element: "refunds",
                filter: JSON.stringify(model),
              });
            }}
            sortModel={transactionSortModel}
            onSortModelChange={(model) => setTransactionSortModel(model)}
            onCellDoubleClick={(ev) => {
              if (ev.row?.journal_entry_id) {
                dispatch(
                  setActiveDialog("add_journal", {
                    id: ev.row.journal_entry_id,
                  })
                );
                return;
              }
              if (ev.row?.split_group_id) {
                dispatch(
                  setActiveDialog("edit_split", {
                    split_group_id: ev.row.split_group_id,
                    id: ev.row.collection_id || ev.row.id,
                  })
                );
                return;
              }
              if (ev.row.collection === "transaction") {
                dispatch(
                  setActiveDialog("edit_transaction", {
                    id: ev.row.collection_id,
                  })
                );
                return;
              }
              if (ev.row.collection === "sale") {
                setDialog({ type: "edit", id: ev.row.collection_id });
                return;
              }
            }}
          />
        )}
      </Box>
      <Edit
        value={
          dialog?.type === "bulk-upload" ||
          dialog?.type === "delete-many" ||
          dialog?.type === "bulk-edit-transactions" ||
          dialog?.type === "bulk-edit-panel"
            ? undefined
            : dialog
        }
        onChange={(dialog) => setDialog(dialog)}
      />
      <BulkEditDialog
        open={dialog?.type === "bulk-upload"}
        onClose={() => setDialog(undefined)}
        entity="refunds"
      />
      <BulkEditTransactionsDialog
        open={dialog?.type === "bulk-edit-transactions"}
        onClose={() => setDialog(undefined)}
      />
      <BulkEditPanel
        open={dialog?.type === "bulk-edit-panel"}
        onClose={() => setDialog(undefined)}
        selectedTransactionIds={editableTransactionIds}
        readOnlyCount={readOnlyCount}
        onOpenCSVBulkEdit={() => setDialog({ type: "bulk-edit-transactions" })}
      />
      <ConfirmDialog
        open={dialog?.type === "delete-many"}
        title="Confirm"
        text={
          dialog?.type === "delete-many"
            ? `Are you sure you want to delete ${dialog.items.length} refund(s)?`
            : ""
        }
        onCancel={() => setDialog(undefined)}
        onConfirm={() => {
          if (dialog?.type === "delete-many") {
            deleteMappedTransactions(dialog.items);
            toast.success("Refund(s) deleted successfully!");
          }
          setDialog(undefined);
        }}
      />
    </div>
  );
}

export default Refunds;
